import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "../components/AdminShell";
import { api, formatPrice } from "../lib/api";
import { Car, Inbox, CheckCircle2, TrendingUp, ArrowRight, Plus, Eye, Users, BarChart3, Search, Heart, Clock, Timer } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Area, AreaChart } from "recharts";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentCars, setRecentCars] = useState([]);
  const [topSearches, setTopSearches] = useState(null);
  const [wishlistDaily, setWishlistDaily] = useState([]);
  const [stock, setStock] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, w, r, c, ts, wl, sk] = await Promise.all([
          api.get("/stats"),
          api.get("/analytics/summary"),
          api.get("/analytics/weekly", { params: { days: 7 } }),
          api.get("/requests"),
          api.get("/cars", { params: { include_sold: true } }),
          api.get("/analytics/search/top", { params: { days: 30, limit: 6 } }).catch(() => ({ data: null })),
          api.get("/admin/wishlist-timeline", { params: { days: 30 } }).catch(() => ({ data: { buckets: [] } })),
          api.get("/admin/stock-analytics").catch(() => ({ data: null })),
        ]);
        setStats(s.data);
        setAnalytics(a.data);
        setWeekly(w.data || []);
        setRecentRequests(r.data.slice(0, 5));
        setRecentCars(c.data.slice(0, 5));
        setTopSearches(ts.data);
        setStock(sk.data);
        // Fill any missing days in the last 30 with count 0 so the chart shows a continuous line
        const buckets = (wl.data && wl.data.buckets) || [];
        const map = new Map(buckets.map((b) => [b.day, b.count]));
        const today = new Date();
        const filled = [];
        for (let i = 29; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          const iso = d.toISOString().slice(0, 10);
          const dayLabel = d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
          filled.push({ day: iso, day_short: dayLabel, count: map.get(iso) || 0 });
        }
        setWishlistDaily(filled);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const cards = stats
    ? [
        { label: "Auto in vendita", value: stats.available_cars, icon: Car, color: "text-gold" },
        { label: "Auto vendute", value: stats.sold_cars, icon: CheckCircle2, color: "text-emerald-400" },
        { label: "Nuove richieste", value: stats.new_requests, icon: Inbox, color: "text-orange-400" },
        { label: "Totale auto", value: stats.total_cars, icon: TrendingUp, color: "text-white" },
      ]
    : [];

  return (
    <AdminShell title="Benvenuto, Rocco">
      {analytics && (
        <div className="mb-6" data-testid="analytics-panel">
          <div className="flex items-center gap-3 mb-4">
            <Eye size={16} className="text-gold" />
            <p className="text-xs uppercase tracking-widest text-white/60">Contatore visite (solo admin)</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-gold/10 border border-gold/30 p-6">
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Visite totali</p>
              <p className="font-display text-4xl font-bold text-gold" data-testid="visits-total">{analytics.total_visits}</p>
            </div>
            <div className="bg-midnight-light border border-white/5 p-6">
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Oggi</p>
              <p className="font-display text-3xl font-bold" data-testid="visits-today">{analytics.visits_today}</p>
            </div>
            <div className="bg-midnight-light border border-white/5 p-6">
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Ultimi 7 giorni</p>
              <p className="font-display text-3xl font-bold">{analytics.visits_7d}</p>
            </div>
            <div className="bg-midnight-light border border-white/5 p-6">
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Ultimi 30 giorni</p>
              <p className="font-display text-3xl font-bold">{analytics.visits_30d}</p>
            </div>
            <div className="bg-midnight-light border border-white/5 p-6">
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1"><Users size={10}/> Visitatori unici</p>
              <p className="font-display text-3xl font-bold">{analytics.unique_visitors}</p>
            </div>
          </div>
        </div>
      )}

      {weekly.length > 0 && (
        <div className="mb-10 bg-midnight-light border border-white/5 p-6" data-testid="visits-chart">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <BarChart3 size={18} className="text-gold" />
              <h2 className="font-display text-xl font-semibold">Visite ultimi 7 giorni</h2>
            </div>
            <p className="text-xs text-white/50">
              Totale settimana: <span className="text-gold font-semibold">{weekly.reduce((a, b) => a + b.visits, 0)}</span>
            </p>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={weekly} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis
                  dataKey="day_short"
                  stroke="rgba(255,255,255,0.5)"
                  tick={{ fontSize: 11, fill: "rgba(255,255,255,0.6)" }}
                  axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
                  tickFormatter={(v, i) => `${v} ${weekly[i]?.day_label || ""}`}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.5)"
                  tick={{ fontSize: 11, fill: "rgba(255,255,255,0.6)" }}
                  axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0a0f1c",
                    border: "1px solid rgba(212, 175, 55, 0.4)",
                    borderRadius: "4px",
                    color: "#fff",
                  }}
                  cursor={{ fill: "rgba(212, 175, 55, 0.1)" }}
                  formatter={(val, name) => [val, name === "visits" ? "Visite" : "Unici"]}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.day_label || label}
                />
                <Bar dataKey="visits" name="visits" fill="#D4AF37" radius={[3, 3, 0, 0]} maxBarSize={70} />
                <Bar dataKey="unique_visitors" name="unique_visitors" fill="#8A6C1F" radius={[3, 3, 0, 0]} maxBarSize={70} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-6 mt-4 text-[10px] uppercase tracking-widest text-white/60">
            <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-gold"></span>Visite totali</span>
            <span className="flex items-center gap-2"><span className="inline-block w-3 h-3" style={{ background: "#8A6C1F" }}></span>Visitatori unici</span>
          </div>
        </div>
      )}

      {wishlistDaily.length > 0 && (() => {
        const total = wishlistDaily.reduce((a, b) => a + b.count, 0);
        const peak = wishlistDaily.reduce((a, b) => (b.count > a.count ? b : a), { count: 0, day_short: "-" });
        return (
          <div className="mb-10 bg-midnight-light border border-white/5 p-6" data-testid="wishlist-chart">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Heart size={18} className="text-gold" fill="currentColor" />
                <h2 className="font-display text-xl font-semibold">Interesse ultimi 30 giorni</h2>
              </div>
              <div className="flex items-center gap-5 text-xs">
                <p className="text-white/50">
                  Totale <span className="text-gold font-semibold">{total}</span> preferiti
                </p>
                {peak.count > 0 && (
                  <p className="text-white/50">
                    Giorno caldo <span className="text-gold font-semibold">{peak.day_short}</span> ({peak.count})
                  </p>
                )}
              </div>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={wishlistDaily} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="wishlistGold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis
                    dataKey="day_short"
                    stroke="rgba(255,255,255,0.5)"
                    tick={{ fontSize: 10, fill: "rgba(255,255,255,0.6)" }}
                    axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
                    interval={4}
                  />
                  <YAxis
                    stroke="rgba(255,255,255,0.5)"
                    tick={{ fontSize: 11, fill: "rgba(255,255,255,0.6)" }}
                    axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0a0f1c",
                      border: "1px solid rgba(212, 175, 55, 0.4)",
                      borderRadius: "4px",
                      color: "#fff",
                    }}
                    cursor={{ stroke: "rgba(212, 175, 55, 0.4)", strokeWidth: 1 }}
                    formatter={(val) => [val, "Preferiti"]}
                    labelFormatter={(l) => `Giorno ${l}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#D4AF37"
                    strokeWidth={2.2}
                    fill="url(#wishlistGold)"
                    dot={{ fill: "#D4AF37", stroke: "#D4AF37", strokeWidth: 2, r: 2.5 }}
                    activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-white/40 mt-3 text-center leading-relaxed">
              Ogni volta che un visitatore aggiunge un'auto ai preferiti viene contato una sola volta. Usa questo grafico per capire in quali giorni pubblicare nuove auto o mandare broadcast.
            </p>
          </div>
        );
      })()}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="bg-midnight-light border border-white/5 p-6" data-testid={`stat-${c.label}`}>
            <c.icon size={20} className={`${c.color} mb-3`} />
            <p className="text-xs uppercase tracking-widest text-white/50 mb-2">{c.label}</p>
            <p className="font-display text-4xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {stock && (stock.active?.length > 0 || stock.sold?.length > 0) && (
        <div className="mb-10 bg-midnight-light border border-white/5 p-6" data-testid="stock-analytics-panel">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-gold" />
              <h2 className="font-display text-xl font-semibold">Giacenza auto</h2>
            </div>
            <div className="flex items-center gap-5 text-xs">
              <p className="text-white/50">
                Media in stock <span className="text-gold font-semibold">{stock.avg_days_active}</span> gg
              </p>
              <p className="text-white/50">
                Media vendita <span className="text-emerald-400 font-semibold">{stock.avg_days_to_sell}</span> gg
              </p>
            </div>
          </div>

          {stock.histogram && stock.histogram.some((h) => h.count > 0) && (
            <div style={{ width: "100%", height: 200 }} className="mb-8">
              <ResponsiveContainer>
                <BarChart data={stock.histogram} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="bucket" stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.6)" }} />
                  <YAxis stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.6)" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#0a0f1c", border: "1px solid rgba(212,175,55,0.4)", borderRadius: 4, color: "#fff" }}
                    formatter={(val) => [val, "Auto vendute"]}
                    labelFormatter={(l) => `${l} giorni`}
                  />
                  <Bar dataKey="count" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={70} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Timer size={14} className="text-gold" />
                <h3 className="text-xs uppercase tracking-widest text-white/60 font-semibold">
                  In stock ({stock.total_active})
                </h3>
              </div>
              {stock.active.length === 0 ? (
                <p className="text-xs text-white/40 py-6 text-center">Nessuna auto attiva</p>
              ) : (
                <ul className="divide-y divide-white/5" data-testid="stock-active-list">
                  {stock.active.slice(0, 8).map((r) => (
                    <li key={r.id} className="flex items-center justify-between py-2.5 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="text-white truncate">
                          {r.brand} {r.model} <span className="text-white/40 text-xs">({r.year})</span>
                        </p>
                        <p className="text-[10px] text-white/40 font-mono">{r.ref_code || "—"}</p>
                      </div>
                      <span className={`shrink-0 ml-3 text-xs font-bold ${r.days_in_stock >= 90 ? "text-red-400" : r.days_in_stock >= 45 ? "text-orange-400" : "text-gold"}`}>
                        {r.days_in_stock} gg
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={14} className="text-emerald-400" />
                <h3 className="text-xs uppercase tracking-widest text-white/60 font-semibold">
                  Vendute ({stock.total_sold})
                </h3>
              </div>
              {stock.sold.length === 0 ? (
                <p className="text-xs text-white/40 py-6 text-center">Nessuna vendita registrata</p>
              ) : (
                <ul className="divide-y divide-white/5" data-testid="stock-sold-list">
                  {stock.sold.slice(0, 8).map((r) => (
                    <li key={r.id} className="flex items-center justify-between py-2.5 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="text-white truncate">
                          {r.brand} {r.model} <span className="text-white/40 text-xs">({r.year})</span>
                        </p>
                        <p className="text-[10px] text-white/40 font-mono">{r.ref_code || "—"}</p>
                      </div>
                      <span className="shrink-0 ml-3 text-xs font-bold text-emerald-400">
                        {r.days_to_sell} gg
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <p className="text-[11px] text-white/40 mt-4 text-center leading-relaxed">
            I giorni sono calcolati dal momento in cui hai pubblicato l'auto fino al momento in cui l'hai marcata come venduta. Le auto vendute prima di questa versione non hanno un timestamp e non compaiono qui.
          </p>
        </div>
      )}

      {topSearches && topSearches.total > 0 && (
        <div className="mb-10 bg-midnight-light border border-white/5 p-6" data-testid="top-searches-panel">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Search size={18} className="text-gold" />
              <h2 className="font-display text-xl font-semibold">Cosa cercano i clienti</h2>
            </div>
            <p className="text-xs text-white/50">
              Ultimi {topSearches.days} giorni · {topSearches.total} ricerche registrate
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <TopList title="Parole ricercate" items={topSearches.queries} empty="Nessuna ricerca testuale" testid="top-queries" />
            <TopList title="Marche più cercate" items={topSearches.brands} empty="Nessun filtro marca" testid="top-brands" />
            <TopList title="Carrozzerie" items={topSearches.body_types} empty="Nessun filtro carrozzeria" testid="top-body-types" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-midnight-light border border-white/5 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display text-xl font-semibold">Ultime auto aggiunte</h2>
            <Link to="/admin/cars" className="text-xs text-gold hover:underline flex items-center gap-1" data-testid="link-view-all-cars">
              Vedi tutte <ArrowRight size={12} />
            </Link>
          </div>
          {recentCars.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-white/50 mb-4">Nessuna auto nel parco</p>
              <Link
                to="/admin/cars/new"
                className="btn-gold inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold"
                data-testid="empty-add-car"
              >
                <Plus size={12} /> Aggiungi Auto
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {recentCars.map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{c.brand} {c.model}</p>
                    <p className="text-xs text-white/50">{c.year} · {c.km.toLocaleString("it-IT")} km</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm text-gold font-semibold">{formatPrice(c.price)}</p>
                    {c.is_sold && <p className="text-[10px] uppercase text-white/40">Venduta</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-midnight-light border border-white/5 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display text-xl font-semibold">Ultime richieste</h2>
            <Link to="/admin/requests" className="text-xs text-gold hover:underline flex items-center gap-1" data-testid="link-view-all-requests">
              Vedi tutte <ArrowRight size={12} />
            </Link>
          </div>
          {recentRequests.length === 0 ? (
            <p className="text-center py-10 text-white/50">Nessuna richiesta ricevuta</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {recentRequests.map((r) => (
                <li key={r.id} className="py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{r.name}</p>
                      <p className="text-xs text-white/50 truncate">
                        {[r.brand, r.model].filter(Boolean).join(" ") || "Auto generica"}
                      </p>
                    </div>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm ${
                      r.status === "new"
                        ? "bg-orange-500/20 text-orange-300"
                        : r.status === "contacted"
                        ? "bg-blue-500/20 text-blue-300"
                        : "bg-white/10 text-white/60"
                    }`}>
                      {r.status === "new" ? "Nuova" : r.status === "contacted" ? "Contattato" : "Chiusa"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

const TopList = ({ title, items, empty, testid }) => {
  if (!items || items.length === 0) {
    return (
      <div data-testid={testid}>
        <p className="text-[10px] uppercase tracking-widest text-white/50 mb-3">{title}</p>
        <p className="text-xs text-white/40 italic">{empty}</p>
      </div>
    );
  }
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-white/50 mb-3">{title}</p>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.label} className="flex items-center gap-3 text-sm" data-testid={`${testid}-item-${it.label}`}>
            <span className="min-w-0 flex-1 truncate text-white/80 capitalize">{it.label}</span>
            <div className="w-20 h-1.5 bg-white/5 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-gold" style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
            <span className="text-xs text-gold font-mono font-semibold min-w-[24px] text-right">{it.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
